import React, { useEffect, useRef, useState } from 'react';
import { Alert, Form, Button, Card, FilterGrid } from '@/components/ui';
import reportService, { EquipmentUsagePreviewData } from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import ExportOptionsModal from '../components/ExportOptionsModal';
import Toast from '../../../components/Toast';
import { daysAgoISO } from '../../../utils/dateUtils';
import { ServiceArea } from '../../../services/areasService';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';

interface EquipmentUsageReportViewProps {
  serviceAreas: ServiceArea[];
  canCreateEquipmentUsage: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const EquipmentUsageReportView: React.FC<EquipmentUsageReportViewProps> = ({ 
  serviceAreas, 
  canCreateEquipmentUsage, 
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceAreaIds: defaultServiceAreaId ? [defaultServiceAreaId] : ([] as string[]),
    startDate: daysAgoISO(28),
    endDate: '',
    equipment: [] as string[]
  });
  const [equipmentOptions, setEquipmentOptions] = useState<string[]>([]);
  // The default service area arrives after the first render; pick it up once.
  useEffect(() => {
    if (defaultServiceAreaId && filters.serviceAreaIds.length === 0) {
      setFilters((prev) => ({ ...prev, serviceAreaIds: [defaultServiceAreaId] }));
    }
  }, [defaultServiceAreaId]);
  // An open-ended date range scans every approved form in the system, so Run needs a start date.
  const missingRequiredFilters = !filters.startDate;
  const serviceAreaParam = filters.serviceAreaIds.join(',');

  // EU-03: the units come from the usage itself, so list them for the chosen service areas and
  // dates in the background - the Equipment filter can then be set before the first Run, not only
  // after it. Debounced so typing a date doesn't fire a query per keystroke; a stale response
  // (filters changed while it was in flight) is dropped.
  const [loadingEquipmentOptions, setLoadingEquipmentOptions] = useState(false);
  const equipmentOptionsRequest = useRef(0);
  useEffect(() => {
    if (missingRequiredFilters || loadingServiceAreas) return;
    const request = ++equipmentOptionsRequest.current;
    const timer = setTimeout(async () => {
      setLoadingEquipmentOptions(true);
      try {
        const response = await reportService.getEquipmentUsagePreviewData({
          serviceArea: serviceAreaParam,
          startDate: filters.startDate,
          endDate: filters.endDate,
          view: 'operations',
          page: 1,
          limit: 1
        });
        if (request === equipmentOptionsRequest.current) setEquipmentOptions(response.equipmentOptions ?? []);
      } catch {
        // Not fatal: the list still fills in when the report runs.
      } finally {
        if (request === equipmentOptionsRequest.current) setLoadingEquipmentOptions(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [serviceAreaParam, filters.startDate, filters.endDate, missingRequiredFilters, loadingServiceAreas]);
  const [previewData, setPreviewData] = useState<EquipmentUsagePreviewData[]>([]); // Current page data
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => {} // Will be overridden
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  // Server-side pagination helper
  const handlePageChange = (page: number, pageSize?: number) => {
    handleGeneratePreview(page, pageSize);
  };

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    handleGeneratePreview(1, newPageSize);
  };

  const handleGeneratePreview = async (
    page: number = 1,
    pageSize?: number,
    // Passed explicitly by handleSort: the sort state set there hasn't landed yet when this runs.
    sort: { field: string; direction: 'asc' | 'desc' } = { field: sortField, direction: sortDirection }
  ) => {
    if (missingRequiredFilters) {
      setErrorMessage('Please choose a start date before running the report.');
      return;
    }
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const response = await reportService.getEquipmentUsagePreviewData({
        serviceArea: serviceAreaParam,
        startDate: filters.startDate,
        endDate: filters.endDate,
        equipment: filters.equipment.length ? filters.equipment.join(',') : undefined,
        view: 'operations',
        page,
        limit: pageSize || pagination.limit,
        sortField: sort.field,
        sortDirection: sort.direction
      });

      // Store only current page data (server-side pagination)
      setPreviewData(response.data);
      if (response.equipmentOptions) setEquipmentOptions(response.equipmentOptions);
      
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
        onPageChange: handlePageChange
      });
      
    } catch (error) {
      setErrorMessage('Failed to generate equipment usage report preview. Please try again.');
      setPreviewData([]);
      setPagination({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
        onPageChange: handlePageChange
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSort = (field: string) => {
    const newDirection = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
    handleGeneratePreview(1, undefined, { field, direction: newDirection }); // Reset to page 1, fetch from server
  };

  const handleExport = async (customFilename?: string, reportType?: string) => {
    setLoadingExport(true);
    try {
      // Finance's export: same rows and format as always (Sage-ready is loaded straight into Sage),
      // so only the service areas and dates apply - the on-screen equipment pick doesn't.
      await reportService.generateEquipmentUsageReport({
        serviceArea: serviceAreaParam,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename,
        reportType
      });

      showSuccessToast('Equipment usage report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export equipment usage report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  // Generate default filename based on format: Equipment Usage - DATE
  const generateDefaultFilename = (reportType: 'simple' | 'sage-ready' = 'simple') => {
    let baseFilename = '';
    if (filters.endDate) {
      const date = new Date(filters.endDate);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      baseFilename = `Equipment Usage_${year}_${month}_${day}`;
    } else {
      // Fallback to current date
      const date = new Date();
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      baseFilename = `Equipment Usage_${year}_${month}_${day}`;
    }
    
    // Append report type suffix
    const suffix = reportType === 'sage-ready' ? '_Sage' : '_Simple';
    return baseFilename + suffix;
  };

  const handleExportWithFilename = async (postedDate: string, customFilename?: string, reportType?: string) => {
    setLoadingExport(true);
    try {
      // Finance's export: same rows and format as always (Sage-ready is loaded straight into Sage),
      // so only the service areas and dates apply - the on-screen equipment pick doesn't.
      await reportService.generateEquipmentUsageReport({
        serviceArea: serviceAreaParam,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename,
        reportType
      });

      showSuccessToast('Equipment usage report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export equipment usage report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  // Operations view: names instead of codes. Employee code and yard activity code are still in
  // both exports, which finance uses.
  const columns = [
    {
      label: 'Date',
      key: 'date',
      sortable: true,
      width: '130px'
    },
    {
      label: 'Employee',
      key: 'employeeName',
      sortable: true,
      width: '170px',
      render: (row: EquipmentUsagePreviewData) => row.employeeName || '-'
    },
    {
      label: 'Foreman Area',
      key: 'foremanArea',
      sortable: true,
      width: '150px',
      render: (row: EquipmentUsagePreviewData) => row.foremanArea || '-'
    },
    {
      label: 'Task Number',
      key: 'taskNumber',
      sortable: true,
      width: '130px',
      render: (row: EquipmentUsagePreviewData) => row.taskNumber || '-'
    },
    {
      label: 'Task Type',
      key: 'taskType',
      sortable: true,
      width: '120px',
      render: (row: EquipmentUsagePreviewData) => row.taskType || '-'
    },
    {
      label: 'Activity Number',
      key: 'activityNumber',
      sortable: false,
      width: '150px',
      render: (row: EquipmentUsagePreviewData) => row.activityNumber || '-'
    },
    { 
      label: 'Vehicle Used', 
      key: 'vehicleUsed',
      sortable: true,
      width: '140px'
    },
    { 
      label: 'Vehicle Hours', 
      key: 'vehicleHours',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Vehicle Rate', 
      key: 'vehicleRate',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Finance Classification', 
      key: 'financeClassification',
      sortable: true,
      width: '180px',
      headerContent: (
        <div className="tw-flex tw-items-center">
          <span>Finance Classification</span>
          <i
            className="bi bi-info-circle tw-text-info tw-ml-1"
            style={{ fontSize: '0.75rem' }} 
            title="From project type when the task has a project; otherwise from task type (quantified, routine, overhead). Labels: Routine, Quantified, H200, Internal, Other Revenue. Sage-ready CSV uses EQ1–EQ5 in the finance column."
          ></i>
        </div>
      )
    }
  ];

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
      <ReportParametersCard
        onGenerate={() => handleGeneratePreview(1)}
        onExport={() => {}} // Remove export functionality from top
        loadingGenerate={loadingPreview}
        loadingExport={false}
        disableExport={true} // Disable export button in top bar
        disableGenerate={missingRequiredFilters}
        generateDisabledReason="Choose a start date first"
        generateButtonText="Run Report"
        hideExportButton={true} // Hide the export button entirely
        defaultCollapsed={false}
        onCollapseChange={setIsFiltersCollapsed} // Track collapse state
      >
        <FilterGrid>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
            <MultiSelectDropdown
              options={serviceAreas}
              value={filters.serviceAreaIds}
              onChange={(selected) => setFilters({ ...filters, serviceAreaIds: selected.map(String) })}
              formatOption={(area) => area.name}
              getValue={(area) => String(area.id)}
              placeholder="All Service Areas"
              disabled={loadingServiceAreas}
            />
          </Form.Group>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Date Range</Form.Label>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Form.Control
                size="sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                style={{ width: '140px' }}
              />
              <span className="tw-text-content-muted tw-text-sm">to</span>
              <Form.Control
                size="sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                style={{ width: '140px' }}
              />
            </div>
          </Form.Group>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Equipment</Form.Label>
            <MultiSelectDropdown
              options={equipmentOptions}
              value={filters.equipment}
              onChange={(selected) => setFilters({ ...filters, equipment: selected.map(String) })}
              formatOption={(unit) => unit}
              getValue={(unit) => unit}
              placeholder={
                equipmentOptions.length
                  ? 'All Equipment'
                  : loadingEquipmentOptions
                    ? 'Loading units…'
                    : missingRequiredFilters
                      ? 'Choose a start date to list units'
                      : 'No units in this date range'
              }
              disabled={equipmentOptions.length === 0}
              noOptionsMessage="No units in this report"
            />
          </Form.Group>
        </FilterGrid>
      </ReportParametersCard>
      </div>
      
      
      {/* Report Data Section - Flexible */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // Important for flex child
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flex: 1,
          minWidth: 0
        }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
          <h6 className="tw-mb-0">Report Data ({pagination.total} records)</h6>
          {pagination.total > 0 && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowExportModal(true)}
              disabled={!canCreateEquipmentUsage}
            >
              <i className="bi bi-download tw-mr-1"></i>
              Export Report
            </Button>
          )}
        </Card.Header>

          {/* Table Container - Scrollable */}
          {/* Table Container - ReportPreviewTable owns its own scroll region (single mechanism,
              see ReportPreviewTable.tsx); no extra overflow wrapper needed here. */}
          <Card.Body className="tw-p-0" style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
          <ReportPreviewTable
            columns={columns}
            data={previewData}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange
            }}
            loading={loadingPreview}
            emptyMessage="No data available. Click 'Run Report' to load data."
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            isFiltersCollapsed={isFiltersCollapsed} // Pass collapse state to table
            yellowHeaderColumn="patrolDate" // Make only Patrol Date column yellow
          />
          </Card.Body>
        </Card>
      </div>

      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onPreliminaryExport={handleExport}
        onFinalExport={handleExportWithFilename}
        loadingPreliminary={loadingExport}
        loadingFinal={loadingExport}
        isEquipmentUsage={true}
        showFilenameInput={true}
        generateEquipmentUsageFilename={generateDefaultFilename}
      />

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type="success"
      />
    </div>
  );
};

export default EquipmentUsageReportView;